<script>
  // Numbered pagination for the CardCollection shell (ADR-0010). Replaces the
  // search page's old load-more/prefetch model with a single numbered-pages
  // model shared by both pages. Pure presentation: it reports the requested
  // page via onPageChange; the shell owns the page state + data fetch.
  import { pageCount } from '../../lib/logic/card-collection-logic.js';

  let { page = 1, total = 0, pageSize = 40, onPageChange } = $props();

  const last = $derived(pageCount(total, pageSize));

  // A compact window of page numbers around the current page, with ellipses.
  const pages = $derived(buildPageList(page, last));

  function buildPageList(current, lastPage) {
    if (lastPage <= 1) return [];
    const out = [];
    const add = (p) => out.push(p);
    const window = 1; // pages on each side of current
    const lo = Math.max(2, current - window);
    const hi = Math.min(lastPage - 1, current + window);
    add(1);
    if (lo > 2) add('…');
    for (let p = lo; p <= hi; p++) add(p);
    if (hi < lastPage - 1) add('…');
    if (lastPage > 1) add(lastPage);
    return out;
  }

  function go(p) {
    if (p < 1 || p > last || p === page) return;
    onPageChange?.(p);
  }
</script>

{#if last > 1}
  <nav class="pagination" aria-label="Pagination">
    <button
      class="page-btn nav"
      disabled={page <= 1}
      onclick={() => go(page - 1)}
      aria-label="Previous page"
    >
      ‹
    </button>
    {#each pages as p, i (i)}
      {#if p === '…'}
        <span class="ellipsis">…</span>
      {:else}
        <button
          class="page-btn"
          class:active={p === page}
          aria-current={p === page ? 'page' : undefined}
          onclick={() => go(p)}
        >
          {p}
        </button>
      {/if}
    {/each}
    <button
      class="page-btn nav"
      disabled={page >= last}
      onclick={() => go(page + 1)}
      aria-label="Next page"
    >
      ›
    </button>
  </nav>
{/if}

<style>
  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 1.5rem 0;
    margin-top: 0.75rem;
    border-top: 1px solid var(--color-border);
    flex-wrap: wrap;
  }

  .page-btn {
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.5rem;
    border: 1px solid var(--color-border-subtle);
    background: var(--color-bg-surface);
    border-radius: 0.375rem;
    cursor: pointer;
    color: var(--color-text);
    font-size: 0.875rem;
    font-family: var(--font-sans);
    font-weight: 500;
    transition:
      background 0.15s,
      border-color 0.15s;
  }

  .page-btn:hover:not(:disabled):not(.active) {
    background: var(--color-accent-bg);
    border-color: var(--color-text-muted);
  }

  .page-btn.active {
    background: var(--color-accent-dark);
    border-color: var(--color-accent-dark);
    color: #fff;
    cursor: default;
  }

  .page-btn:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .ellipsis {
    color: var(--color-text-tertiary);
    padding: 0 0.25rem;
  }
</style>
